import React, { useState, useEffect, useRef } from "react";
import { DownArrow } from "../../../../assets/icons/DownArrow";

export interface CountryOption {
  id: number;
  name: string;
  iso2: string;
}

const getFlagImageUrl = (iso2: string): string => {
  return `https://flagcdn.com/w320/${iso2.toLowerCase()}.png`;
};

const CountryDropdown: React.FC<{
  options: CountryOption[];
  onChange: (option: CountryOption | null) => void;
  defaultValue?: CountryOption | null;
  invalidInputCheck?: boolean;
  hasClassString?: boolean;
  classString?: string;
  placeholder?: string;
  showIcon?: boolean;
  optionsClassString?: string;
}> = ({
  options,
  onChange,
  defaultValue,
  invalidInputCheck,
  hasClassString,
  classString,
  placeholder = "Select Country",
  showIcon = true,
  optionsClassString,
}) => {
  const [selectedOption, setSelectedOption] = useState<CountryOption | null>(
    defaultValue || null,
  );
  const [showOptions, setShowOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOption(defaultValue ?? null);
    setSearchTerm(defaultValue ? defaultValue.name : "");
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleSelect = (option: CountryOption) => {
    setSelectedOption(option);
    setSearchTerm(option.name);
    setShowOptions(false);
    onChange(option);
  };

  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm("");
    onChange(null); // Notify parent of the cleared selection
  };

  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="relative w-full space-y-[2px]" ref={dropdownRef}>
      <div className="relative">
        {showIcon && selectedOption && searchTerm && (
          <img
            src={getFlagImageUrl(selectedOption.iso2)}
            alt={selectedOption.name}
            className="absolute left-3 top-[52%] transform -translate-y-1/2"
            style={{ width: "19px", height: "12px" }}
          />
        )}
        <input
          type="text"
          className={`${!hasClassString ? "h-[48px] w-full p-3 border border-gray-400 rounded-[5px] placeholder:text-[16px] placeholder-slate-500" : classString} ${showIcon && selectedOption && searchTerm ? "pl-10" : ""} ${invalidInputCheck && "border-red-500"}`}
          placeholder={placeholder}
          value={searchTerm}
          onClick={() => setShowOptions(!showOptions)}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (e.target.value === "") handleClear(); // Clear selection if input is empty
          }}
          onKeyDown={(e) => {
            if (
              (e.key === "Backspace" || e.key === "Delete") &&
              searchTerm.length > 0
            ) {
              setSearchTerm(""); // Clear the input completely on Backspace or Delete
              handleClear();
            }
          }}
        />
        <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <DownArrow />
        </span>
      </div>

      {showOptions && (
        <ul className="absolute z-50 w-full bg-white border border-gray-300 rounded-md max-h-60 overflow-y-auto">
          {filteredOptions.map((option) => (
            <li
              key={option.iso2}
              className="flex items-center p-2 cursor-pointer hover:bg-gray-200"
              onClick={() => handleSelect(option)}
            >
              {showIcon && (
                <img
                  src={getFlagImageUrl(option.iso2)}
                  alt={option.name}
                  style={{ width: "19px", height: "12px", marginRight: "10px" }}
                />
              )}
              <span className={`${optionsClassString}`}>{option.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CountryDropdown;
